<script setup lang="ts">
import { useRoute, RouterLink } from "vue-router";
import { ref, onMounted } from "vue";
import RvApi from "@/rvapi";
import type { User, Device } from "@/rvapi";
import { utc2local } from "@/utils";
import LogList from "@/components/LogList.vue";
import LogWriter from "@/components/LogWriter.vue";
import OrderList from "@/components/OrderList.vue";

// todo remove from newsletter function
const migrating = ref(false);
const route = useRoute();
const userId = route.params.id as string;
const deviceId = ref(undefined as string | undefined);
const userData = ref({} as User);
const devices = ref([] as Device[]);
const loaded = ref(false);
onMounted(async () => {
  const userpromise = RvApi.getUser(userId);
  const devicepromise = RvApi.searchDevices(userId);
  userData.value = await userpromise;
  devices.value = await devicepromise;
  if (devices.value && devices.value.length > 0) {
    deviceId.value = devices.value[0].deviceId;
  }
  loaded.value = true;
});
const migrateBalance = async (device: Device) => {
  migrating.value = true;
  const result = await RvApi.migrateBalanceToUser(
    userData.value.userId,
    device.deviceId,
  );
  if (result.balance) {
    userData.value.legacyBalance = result.balance;
    device.legacyBalance = 0;
  }
  migrating.value = false;
};
</script>

<template>
  <h1>User</h1>
  <pn-spinner size="4" v-show="!loaded" />
  <section id="user-details" v-show="!!loaded">
    <h2>Details</h2>
    <dl>
      <dt>Email</dt>
      <dd>{{ userData.email }}</dd>
      <dt>Created at</dt>
      <dd>{{ utc2local(userData.createdAt) }}</dd>
      <dt>Last login at</dt>
      <dd>{{ utc2local(userData.lastLoginAt) }}</dd>
      <dt>Signed up for newsletter</dt>
      <dd>{{ userData.signedUpForNewsletter ? "Yes" : "No" }}</dd>
      <dt>Consent to save receiver history</dt>
      <dd>{{ userData.saveReceivers ? "Yes" : "No" }}</dd>
      <dd v-if="userData.saveReceivers">
        <RouterLink :to="'/user/' + userId + '/receivers'">
          View saved receivers
        </RouterLink>
      </dd>
      <dt v-if="userData.legacyBalance">Legacy Balance</dt>
      <dd v-if="userData.legacyBalance">{{ userData.legacyBalance }}</dd>
    </dl>
  </section>
  <section v-show="!!loaded">
    <h3>Devices</h3>
    <pn-table bordered="true" striped="true">
      <table>
        <thead>
          <tr>
            <th>Ref.</th>
            <th>Device</th>
            <th>User</th>
            <th>Last used</th>
            <th>Balance</th>
            <th>Language</th>
            <th>App</th>
            <th>Device</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="device in devices" :key="device.deviceId">
            <td>{{ device.supportRef }}</td>
            <td>
              <RouterLink :to="'/device/' + device.deviceId">{{
                device.deviceId?.substring(0, 8) + "..."
              }}</RouterLink>
            </td>
            <td>
              {{ device.userId }}
            </td>
            <td>{{ utc2local(device.lastRegistration) }}</td>

            <td>
              {{ device.legacyBalance }}
              <span v-if="device.legacyBalance">
                <pn-button
                  :loading="migrating"
                  small="true"
                  appearance="warning"
                  @click="migrateBalance(device)"
                  >Migrate to current user</pn-button
                >
              </span>
            </td>
            <td>{{ device.language }}</td>
            <td>{{ device.platform }} {{ device.appVersion }}</td>
            <td>
              {{ device.deviceOsVersion }}
              {{ device.deviceDescription }}
            </td>
          </tr>
        </tbody>
      </table>
    </pn-table>
  </section>
  <section v-show="!!loaded">
    <h2>Consumed promotion codes</h2>
    <pn-table bordered="true" striped="true">
      <table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Consumed at</th>
            <th>Legacy</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="code in userData.consumedPromotionCodes"
            :key="code.promotionCode"
          >
            <td>{{ code.promotionCode }}</td>
            <td>{{ utc2local(code.consumedAt) }}</td>
            <td>{{ code.isLegacyCode }}</td>
          </tr>
        </tbody>
      </table>
    </pn-table>
  </section>
  <section v-if="!!loaded">
    <OrderList type="user" :searchId="userId" />
  </section>
  <section v-if="!!loaded && deviceId">
    <LogWriter :deviceId="deviceId" :userId="userId" />
  </section>
  <section v-if="!!loaded">
    <LogList type="user" :searchId="userId" />
  </section>
</template>
